import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Shield,
  Award,
  Mail,
  Calendar,
  Clock,
  Ticket,
  Monitor,
  BookOpen,
  ShoppingCart,
  Boxes,
  Settings,
  Phone,
  LayoutDashboard,
  Users,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { partnerService } from '../../services/partnerService';
import { AdminPermissions, CustomerUserDetail, User } from '../../types/partner';

/**
 * Partner-side view of one customer user. Mirrors what a super admin sees in
 * aitryx-customer's UserEditModal (role, module permissions, marketplace access
 * level) so partner staff can troubleshoot and correct a user's access.
 *
 * Reads and writes go through the partner routes in core, which store the
 * permissions in the same `user-permissions/{userId}` blob the customer app uses.
 */

type AdminModuleKey = Exclude<
  keyof AdminPermissions,
  'marketplaceCanOrder' | 'marketplaceApprover' | 'marketplaceOrderThreshold'
>;

// Same modules, labels and order as the customer app's UserEditModal.
const MODULES: { key: AdminModuleKey; label: string; desc: string; icon: React.ReactNode }[] = [
  { key: 'adminTickets', label: 'Admin Tickets', desc: 'Manage admin support tickets', icon: <Ticket className="h-4 w-4" /> },
  { key: 'devices', label: 'Device Management', desc: 'Manage and monitor devices', icon: <Monitor className="h-4 w-4" /> },
  { key: 'knowledgeBase', label: 'Knowledge Base', desc: 'Manage documents and KB', icon: <BookOpen className="h-4 w-4" /> },
  { key: 'marketplace', label: 'Marketplace', desc: 'Access app marketplace', icon: <ShoppingCart className="h-4 w-4" /> },
  { key: 'msAdmin', label: 'Microsoft Admin', desc: 'Microsoft 365 administration', icon: <Boxes className="h-4 w-4" /> },
  { key: 'settings', label: 'Settings', desc: 'Customize app settings', icon: <Settings className="h-4 w-4" /> },
  { key: 'telstra', label: 'Telstra Services', desc: 'Telstra mobile management', icon: <Phone className="h-4 w-4" /> },
  { key: 'ticketDashboard', label: 'Ticketing Dashboard', desc: 'View ticketing analytics', icon: <LayoutDashboard className="h-4 w-4" /> },
  { key: 'userManagement', label: 'User Management', desc: 'Manage users and permissions', icon: <Users className="h-4 w-4" /> },
];

// What the customer app grants a freshly promoted admin.
const DEFAULT_ADMIN_PERMISSIONS: AdminPermissions = {
  adminTickets: true,
  devices: true,
  knowledgeBase: true,
  marketplace: true,
  marketplaceCanOrder: true,
  marketplaceApprover: false,
  marketplaceOrderThreshold: null,
  msAdmin: true,
  settings: true,
  telstra: true,
  ticketDashboard: true,
  userManagement: true,
};

interface EditForm {
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  status: 'Active' | 'Inactive';
  adminPermissions: AdminPermissions;
}

interface CustomerUserDetailModalProps {
  partnerId: string;
  customerId: string;
  user: User;
  onClose: () => void;
  /** Called after a successful save with the user as the server now has it. */
  onSaved: (user: User) => void;
}

function formToState(user: User, perms: AdminPermissions | undefined): EditForm {
  return {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    isAdmin: !!user.isAdmin,
    isSuperAdmin: !!user.isSuperAdmin,
    status: user.status === 'Inactive' ? 'Inactive' : 'Active',
    adminPermissions: { ...(perms || {}) },
  };
}

/**
 * Normalise to the explicit true/false shape the customer app writes, so the
 * stored blob never carries nulls that fail-closed checks would read as denied.
 */
function normalisePermissions(form: EditForm): AdminPermissions {
  if (!form.isAdmin) return {};
  const p = form.adminPermissions;
  const marketplaceOn = p.marketplace === true;
  const canOrder = marketplaceOn && p.marketplaceCanOrder !== false;
  const rawThreshold = p.marketplaceOrderThreshold;
  const threshold =
    canOrder && typeof rawThreshold === 'number' && Number.isFinite(rawThreshold) && rawThreshold >= 0
      ? rawThreshold
      : null;
  return {
    adminTickets: p.adminTickets === true,
    devices: p.devices === true,
    knowledgeBase: p.knowledgeBase === true,
    marketplace: marketplaceOn,
    marketplaceCanOrder: canOrder,
    marketplaceApprover: marketplaceOn && p.marketplaceApprover === true,
    marketplaceOrderThreshold: threshold,
    msAdmin: p.msAdmin === true,
    settings: p.settings === true,
    telstra: p.telstra === true,
    ticketDashboard: p.ticketDashboard === true,
    userManagement: p.userManagement === true,
  };
}

export function CustomerUserDetailModal({ partnerId, customerId, user, onClose, onSaved }: CustomerUserDetailModalProps) {
  const [detail, setDetail] = useState<CustomerUserDetail | null>(null);
  const [form, setForm] = useState<EditForm>(() => formToState(user, undefined));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    partnerService
      .getCustomerUserDetail(partnerId, customerId, user.userId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setDetail(res.data);
          setForm(formToState(res.data.user, res.data.permissions?.adminPermissions));
        } else {
          setError(res.error || 'Failed to load user details');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load user details');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId, customerId, user.userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tenantFeatures = detail?.tenantFeatures || {};
  const featureKnown = (key: string) => Object.prototype.hasOwnProperty.call(tenantFeatures, key);
  const featureEnabled = (key: string) => !featureKnown(key) || tenantFeatures[key] === true;

  const enabledModuleCount = useMemo(
    () => MODULES.filter((m) => form.adminPermissions[m.key] === true).length,
    [form.adminPermissions]
  );

  const marketplaceEnabled = form.adminPermissions.marketplace === true;
  const canPlaceOrders = marketplaceEnabled && form.adminPermissions.marketplaceCanOrder !== false;
  const thresholdValue = form.adminPermissions.marketplaceOrderThreshold;
  const thresholdText = thresholdValue == null || Number.isNaN(thresholdValue) ? '' : String(thresholdValue);

  const setPerm = (patch: Partial<AdminPermissions>) =>
    setForm((f) => ({ ...f, adminPermissions: { ...f.adminPermissions, ...patch } }));

  const toggleModule = (key: AdminModuleKey) => setPerm({ [key]: !(form.adminPermissions[key] === true) });

  const setAdmin = (checked: boolean) =>
    setForm((f) => ({
      ...f,
      isAdmin: checked,
      // Dropping admin drops super admin too (the server enforces the same).
      isSuperAdmin: checked ? f.isSuperAdmin : false,
      // New admins start with everything on, as in the customer app.
      adminPermissions: checked
        ? Object.keys(f.adminPermissions).length > 0
          ? f.adminPermissions
          : { ...DEFAULT_ADMIN_PERMISSIONS }
        : {},
    }));

  // Super admins usually have no stored module blob (they bypass it), so when one
  // is demoted to a plain admin start from the full default set rather than
  // leaving them with nothing selected.
  const setSuperAdmin = (checked: boolean) =>
    setForm((f) => ({
      ...f,
      isSuperAdmin: checked,
      isAdmin: checked ? true : f.isAdmin,
      adminPermissions:
        Object.keys(f.adminPermissions).length === 0 ? { ...DEFAULT_ADMIN_PERMISSIONS } : f.adminPermissions,
    }));

  const selectAll = () => setPerm({ ...DEFAULT_ADMIN_PERMISSIONS, marketplaceOrderThreshold: thresholdValue ?? null });
  const clearAll = () => {
    const off: AdminPermissions = {};
    MODULES.forEach((m) => { off[m.key] = false; });
    off.marketplaceCanOrder = false;
    off.marketplaceApprover = false;
    off.marketplaceOrderThreshold = null;
    setPerm(off);
  };

  const setThreshold = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setPerm({ marketplaceOrderThreshold: null });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setPerm({ marketplaceOrderThreshold: parsed });
  };

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }
    const original = detail?.user || user;
    const roleChanged = original.isAdmin !== form.isAdmin || !!original.isSuperAdmin !== form.isSuperAdmin;
    if (roleChanged) {
      const summary = form.isSuperAdmin
        ? 'Super Admin'
        : form.isAdmin
          ? 'Administrator'
          : 'a standard user (no admin modules)';
      if (!window.confirm(`Change ${original.firstName} ${original.lastName}'s role to ${summary}?`)) return;
    }

    try {
      setSaving(true);
      setError(null);

      const userRes = await partnerService.updateCustomerUser(partnerId, customerId, user.userId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        isAdmin: form.isAdmin,
        isSuperAdmin: form.isSuperAdmin,
        status: form.status,
      });
      if (!userRes.success) throw new Error(userRes.error || 'Failed to update user');

      const permRes = await partnerService.updateCustomerUserPermissions(partnerId, customerId, user.userId, {
        adminPermissions: normalisePermissions(form),
      });
      if (!permRes.success) throw new Error(permRes.error || 'Failed to update permissions');

      onSaved({ ...original, ...(userRes.data || {}) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const current = detail?.user || user;
  const initials = `${current.firstName?.charAt(0) || ''}${current.lastName?.charAt(0) || current.email.charAt(0)}`.toUpperCase();
  const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString() : 'Never');
  const modifiedBy = detail?.permissions?.modifiedBy;
  const lastModified = detail?.permissions?.lastModified;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col lg:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`User ${current.firstName} ${current.lastName}`}
      >
        {/* Left rail: identity and account facts */}
        <div className="lg:w-[320px] shrink-0 bg-slate-50 border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col">
          <div className="px-6 pt-6 pb-4 flex items-start justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-700 text-lg font-semibold shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-900 truncate">{current.firstName} {current.lastName}</h2>
                <p className="text-sm text-slate-500 truncate flex items-center gap-1"><Mail className="h-3 w-3 shrink-0" />{current.email}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors lg:hidden" title="Close" aria-label="Close dialog">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          <div className="px-6 pb-4 flex flex-wrap gap-1.5">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
              form.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${form.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              {form.status}
            </span>
            {form.isSuperAdmin ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-600/20"><Award className="h-3 w-3" />Super Admin</span>
            ) : form.isAdmin ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 ring-1 ring-orange-600/20"><Shield className="h-3 w-3" />Administrator</span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">User</span>
            )}
          </div>

          <div className="h-px bg-slate-200 mx-6" />

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Account</p>
              <dl className="space-y-2.5 text-xs">
                <div className="flex justify-between gap-3"><dt className="text-slate-500">User ID</dt><dd className="font-mono text-slate-700 truncate" title={current.userId}>{current.userId}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500 flex items-center gap-1"><Calendar className="h-3 w-3" />Created</dt><dd className="text-slate-700">{fmtDate(current.createdAt)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />Last sign-in</dt><dd className="text-slate-700">{fmtDate(current.lastLoginAt)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Microsoft linked</dt><dd className="text-slate-700">{current.msObjectId ? 'Yes' : 'No'}</dd></div>
                {detail?.subscriptionTier && (
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Tenant plan</dt><dd className="text-slate-700">{detail.subscriptionTier}</dd></div>
                )}
              </dl>
            </div>

            {(lastModified || modifiedBy) && (
              <>
                <div className="h-px bg-slate-200" />
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Permissions last changed</p>
                  <p className="text-xs text-slate-700">{fmtDate(lastModified)}</p>
                  {modifiedBy && <p className="text-xs text-slate-500 break-all mt-0.5">by {modifiedBy}</p>}
                </div>
              </>
            )}

            <div className="h-px bg-slate-200" />

            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
              form.status === 'Inactive' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
            }`}>
              <input
                type="checkbox"
                checked={form.status === 'Inactive'}
                onChange={(e) => setForm({ ...form, status: e.target.checked ? 'Inactive' : 'Active' })}
                className="w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
              />
              <div>
                <div className="text-sm font-medium text-slate-900">Mark as inactive</div>
                <div className="text-xs text-slate-500">User cannot sign in</div>
              </div>
            </label>
          </div>
        </div>

        {/* Right pane: editable role and permissions */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
            <div>
              <h3 className="font-semibold text-slate-900">Edit User</h3>
              <p className="text-xs text-slate-500 mt-0.5">Role and module permissions as seen by super admins in Aitryx</p>
            </div>
            <button onClick={onClose} className="hidden lg:flex p-2 hover:bg-slate-100 rounded-lg transition-colors" title="Close" aria-label="Close dialog">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />{error}
              </div>
            )}

            {loading ? (
              <div className="py-12 text-center">
                <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-500">Loading permissions...</p>
              </div>
            ) : (
              <>
                {/* Details */}
                <section>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">User details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">First name</label>
                      <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Last name</label>
                      <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </section>

                {/* Role */}
                <section>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Admin access</h4>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                      form.isAdmin ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.isAdmin}
                        onChange={(e) => setAdmin(e.target.checked)}
                        className="w-5 h-5 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><Shield className="h-4 w-4 text-orange-600" />Administrator</div>
                        <p className="text-xs text-slate-500">Access to the admin modules selected below</p>
                      </div>
                      {form.isAdmin && !form.isSuperAdmin && (
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-800 text-white shrink-0">
                          {enabledModuleCount} of {MODULES.length} modules
                        </span>
                      )}
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                      form.isSuperAdmin ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.isSuperAdmin}
                        onChange={(e) => setSuperAdmin(e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><Award className="h-4 w-4 text-purple-600" />Super Admin</div>
                        <p className="text-xs text-slate-500">Unrestricted access to every module, and can manage other admins in Aitryx</p>
                      </div>
                    </label>
                  </div>
                </section>

                {/* Modules */}
                {form.isSuperAdmin ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3">
                    <Info className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-purple-900">Super Administrator</div>
                      <p className="text-xs text-purple-800 mt-1">
                        Super admins bypass module permissions in Aitryx, so the module list does not apply. Untick Super Admin to manage modules individually.
                      </p>
                    </div>
                  </div>
                ) : form.isAdmin ? (
                  <section>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Module permissions</h4>
                      <div className="flex items-center gap-2 text-xs">
                        <button type="button" onClick={selectAll} className="text-orange-600 hover:text-orange-700 font-medium">Select all</button>
                        <span className="text-slate-300">·</span>
                        <button type="button" onClick={clearAll} className="text-slate-500 hover:text-slate-700">Clear all</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {MODULES.map((mod) => {
                        const checked = form.adminPermissions[mod.key] === true;
                        const planDisabled = !featureEnabled(mod.key);
                        return (
                          <label
                            key={mod.key}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                              checked ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                              checked ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {mod.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                {mod.label}
                                {planDisabled && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-600/20" title="The tenant feature flag for this module is off, so the user will not see it even if granted">
                                    Off for tenant
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500">{mod.desc}</div>
                            </div>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleModule(mod.key)}
                              className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500 shrink-0"
                            />
                          </label>
                        );
                      })}
                    </div>

                    {marketplaceEnabled && (
                      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <ShoppingCart className="h-4 w-4 text-slate-500" />
                          <h5 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Marketplace access level</h5>
                        </div>

                        <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={form.adminPermissions.marketplaceCanOrder !== false}
                            onChange={() => setPerm({ marketplaceCanOrder: !(form.adminPermissions.marketplaceCanOrder !== false) })}
                            className="mt-0.5 w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900">Can place orders directly</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              When off, this user's orders become <span className="font-medium">requests</span> that a marketplace approver or super admin must sign off on.
                            </div>
                          </div>
                        </label>

                        <div className={`ml-7 mt-2 p-3 rounded-lg border border-slate-200 transition-opacity ${canPlaceOrders ? '' : 'opacity-50'}`}>
                          <label htmlFor="partner-marketplace-order-threshold" className="block text-sm font-medium text-slate-900">Approval threshold</label>
                          <p className="text-xs text-slate-500 mt-0.5 mb-2">
                            Orders above this amount need approval. Leave blank for no limit.
                            {!canPlaceOrders && <span className="block mt-1">Enable "Can place orders directly" above to set a threshold.</span>}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">AUD $</span>
                            <input
                              id="partner-marketplace-order-threshold"
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              placeholder="No limit"
                              value={thresholdText}
                              disabled={!canPlaceOrders}
                              onChange={(e) => setThreshold(e.target.value)}
                              className="w-40 px-3 py-1.5 text-sm rounded-md border border-slate-200 bg-white text-slate-900 disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                            />
                          </div>
                        </div>

                        <label className="flex items-start gap-3 p-3 mt-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={form.adminPermissions.marketplaceApprover === true}
                            onChange={() => setPerm({ marketplaceApprover: !(form.adminPermissions.marketplaceApprover === true) })}
                            className="mt-0.5 w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900">Can approve marketplace requests</div>
                            <div className="text-xs text-slate-500 mt-0.5">Sees the full Requests inbox and can approve or reject other users' pending orders.</div>
                          </div>
                        </label>
                      </div>
                    )}
                  </section>
                ) : (
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 text-center">This user is not an administrator and has no access to admin modules.</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 shrink-0">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || loading}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
